<template>
  <div class="orcamentos-do-cliente-table">
    <div class="tab-content">
      <data-table
        :config="tableConfig"
        :data="tableData"
        :filterConfig="filterConfig"
        :refreshButton="refreshButton"
        @row-click="handleRowClick"
        @edit-click="handleEditClick"
        @refresh="loadData">
      </data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useOrcamento } from '../../composables/useOrcamento';
import type { Orcamentos_BuscaOrcamentosDoClienteRow } from '../../contracts/Orcamentos_BuscaOrcamentosDoClienteRow';

type Id = number | string;

// Backend row shape comes from the contract. UI-only extras (`ID` for grid
// row identity, `_isFrozenFooter` for footer rendering) are layered on top.
// No `[key: string]: unknown` index signature — that conflicted with the
// strict contract row when assigning `tableData.value = orcamentos`.
interface OrcamentoRow extends Partial<Orcamentos_BuscaOrcamentosDoClienteRow> {
  ID?: Id;
  _isFrozenFooter?: boolean;
}

interface CellParams {
  value: unknown;
  data: OrcamentoRow;
}

// Props
const props = defineProps<{
  clienteId: Id;
}>();

// Composables
const { t } = useI18n();
const { fetchOrcamentosByCliente } = useOrcamento();

// Reactive data
const tableData = ref<OrcamentoRow[]>([]);

// Computed
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaOrcamentosCliente',
    pageLength: 100,
    // Disable filters
    showFilters: false,
    // Disable sorting
    sortable: false,
    columns: tableColumns.value,
    pagination: false,
    stripedRows: true,
    showGridlines: true,
    resizableColumns: true,
    size: 'small',
    heightMode: 'fill-parent'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'orcamento_id': 'text',
      'status_label': 'select',
      'orcamento_data': 'date',
      'Localidade': 'select',
      'orcamento_valor': 'number'
    }
  };
});

const refreshButton = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'orcamento_id',
      title: t('ID'),
      renderType: 'clickable_id',
      width: 100
    },
    {
      data: 'status_label',
      title: t('Status'),
      width: 200,
      cellRenderer: (params: CellParams): string => {
        // For frozen footer row, show raw value
        if (params.data && params.data._isFrozenFooter) {
          return params.value != null ? String(params.value) : '';
        }

        let severity = '';
        let icon = '';

        switch (params.data.orcamento_status) {
          case 'Pendente':
            severity = 'warning';
            icon = 'exclamation-triangle';
            break;
          case 'Respondido':
            severity = 'info';
            icon = 'clock';
            break;
          case 'Aprovado':
            severity = 'success';
            icon = 'check';
            break;
          case 'Parcialmente concluído':
            severity = 'info';
            icon = 'check';
            break;
          case 'Todos os serviços realizados':
            severity = 'primary';
            icon = 'flag';
            break;
          case 'Cancelado':
            severity = 'secondary';
            icon = 'times';
            break;
          default:
            severity = 'secondary';
            icon = 'question';
        }

        return `<span class='p-tag p-tag-${severity}'><i class='pi pi-${icon} p-tag-icon'></i> <span class="p-tag-value">${params.value}</span></span>`;
      }
    },
    {
      data: 'orcamento_data',
      title: t('Data'),
      width: 180,
      renderType: 'datetime'
    },
    {
      data: 'Localidade',
      title: t('Local'),
      width: 150
    },
    {
      data: 'orcamento_valor',
      title: t('Total'),
      width: 150,
      renderType: 'currency',
      footerType: 'sum'
    }
  ];
});

// Methods
const loadData = async (): Promise<void> => {
  log('OrcamentosDoClienteTable', 'loadData', 'Loading orcamentos for client', props.clienteId);

  if (!props.clienteId || props.clienteId === 0) {
    tableData.value = [];
    return;
  }

  try {
    const orcamentos = await fetchOrcamentosByCliente(Number(props.clienteId));
    tableData.value = orcamentos || [];
    log('OrcamentosDoClienteTable', 'loadData', 'Loaded records', tableData.value.length);
  } catch (error) {
    log('OrcamentosDoClienteTable', 'loadData', 'Error loading orcamentos', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar orçamentos: ') + message);
    tableData.value = [];
  }
};

const handleRowClick = (event: unknown): void => {
  const rowData = event as OrcamentoRow | undefined;
  log('OrcamentosDoClienteTable', 'handleRowClick', 'Row clicked', rowData?.ID || null);
};

const handleEditClick = (orcamentoId: Id): void => {
  log('OrcamentosDoClienteTable', 'handleEditClick', 'Edit clicked', orcamentoId);
  editarOrcamento(orcamentoId);
};

const editarOrcamento = (orcamentoId: Id): void => {
  log('OrcamentosDoClienteTable', 'editarOrcamento', 'Opening orcamento', orcamentoId);
  // Emit event to open orcamento modal
  eventBus.emit('orcamento.edit', orcamentoId);
};

// Lifecycle
onMounted(() => {
  log('OrcamentosDoClienteTable', 'mounted', 'Component mounted');
  loadData();
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.orcamentos-do-cliente-table {
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tab-content {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
</style>

